import { createContext, useContext, useState, useCallback, lazy, Suspense, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import lazyWithReload from '../../utils/lazyWithReload';
import DrawerLoadingFallback from '../common/DrawerLoadingFallback';
const CrmQuotationDrawer = lazyWithReload(() => import('./CrmQuotationDrawer'), 'CrmQuotationDrawer');
import { sid } from '../../utils/shortId';
import { registerEntityOpener } from '../../utils/openEntity';
import { noProviderOpener } from '../../utils/drawerNoProvider';
import { providerIfModuleOn } from '../../lib/hostModules';

/**
 * URL-driven `?quotationId=<short>`.
 * API: useCrmQuotationDrawer() → { openQuotation, closeQuotation, version, bumpVersion, openQuotationId }
 */
const Ctx = createContext(null);

// Provider không nằm trên cây React của người gọi (hay gặp nhất: nội dung xem TRONG một
// drawer KHÁC) → lui về sổ đăng ký module-scope. Xem utils/drawerNoProvider.
const NO_PROVIDER = {
  openQuotation: noProviderOpener('quotation'),
  closeQuotation: () => {},
  version: 0,
  bumpVersion: () => {},
  openQuotationId: null,
};

function CrmQuotationDrawerProviderOn({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const quotationId = searchParams.get('quotationId');
  const [version, setVersion] = useState(0);

  const openQuotation = useCallback((id) => {
    if (!id) return;
    const short = sid(id);
    setSearchParams(prev => {
      if (prev.get('quotationId') === short) return prev;
      const next = new URLSearchParams(prev);
      next.set('quotationId', short);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeQuotation = useCallback(() => {
    setSearchParams(prev => {
      if (!prev.get('quotationId')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('quotationId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const bumpVersion = useCallback(() => setVersion(v => v + 1), []);

  // Khai báo với kernel utils/openEntity: "loại này mở bằng POPUP" → link/card trong chat
  // trỏ tới bản ghi loại này bung popup tại chỗ, không nhảy trang chi tiết (trắng trang chờ chunk).
  useEffect(() => registerEntityOpener('quotation', openQuotation), [openQuotation]);

  return (
    <Ctx.Provider value={{ openQuotation, closeQuotation, version, bumpVersion, openQuotationId: quotationId }}>
      {children}
      {quotationId && (
        <Suspense fallback={<DrawerLoadingFallback onClose={closeQuotation} />}>
          <CrmQuotationDrawer open={!!quotationId} quotationId={quotationId} onClose={closeQuotation} />
        </Suspense>
      )}
    </Ctx.Provider>
  );
}

export function useCrmQuotationDrawer() {
  return useContext(Ctx) || NO_PROVIDER;
}

// Cụm CRM tắt ở host (`modules.crm = false` — Site Connect 12/10/2026) ⇒ Provider rỗng: không đăng ký popup, không
// đọc query param, không dựng drawer (nội dung drawer gọi hàng chục /crm/*). Xem lib/hostModules.
export const CrmQuotationDrawerProvider = providerIfModuleOn('crm', CrmQuotationDrawerProviderOn);
